import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type BlueBirdPromise from 'bluebird';

import { Analytics } from '@trello/atlassian-analytics';
import { ApiError } from '@trello/error-handling';
import { intl } from '@trello/i18n';
import { localizeServerError } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import type { PIIString } from '@trello/privacy';
import type { InvitationLinkPageIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { SwitchAccountsLink } from './SwitchAccountsLink';

interface ConfirmedUserTextProps {
  fullName: PIIString;
  returnUrl: string;
  modelName: 'Board' | 'Team';
  analyticsContainers: {
    [key: string]: {
      id: string;
    };
  };
  fxJoin: (traceId: string) => BlueBirdPromise<void>;
  setErrorMessage: (message: string) => void;
  setIsEnterpriseError: (hasError: boolean) => void;
  setIsWorkspaceInviteRestrictionError: (hasError: boolean) => void;
}

export const ConfirmedUserText: FunctionComponent<ConfirmedUserTextProps> = ({
  fullName,
  returnUrl,
  modelName,
  fxJoin,
  setErrorMessage,
  setIsEnterpriseError,
  setIsWorkspaceInviteRestrictionError,
  analyticsContainers,
}) => {
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const handleOnJoin = useCallback(() => {
    const taskName =
      modelName === 'Board'
        ? 'edit-board/members/accept-invite'
        : 'edit-organization/members/accept-invite';
    const source =
      modelName === 'Board'
        ? 'acceptBoardInvitationScreen'
        : 'acceptTeamInvitationScreen';

    const traceId = Analytics.startTask({
      taskName,
      source,
    });

    const buttonName =
      modelName === 'Board' ? 'joinBoardButtonV2' : 'joinTeamButtonV2';

    Analytics.sendClickedButtonEvent({
      buttonName,
      source,
      containers: analyticsContainers,
    });

    setButtonDisabled(true);

    return fxJoin(traceId)
      .then(() => {
        Analytics.taskSucceeded({
          taskName,
          source,
          traceId,
        });
      })
      .catch(ApiError.NotFound, (error: Error) => {
        Analytics.taskFailed({
          taskName,
          source,
          traceId,
          error,
        });

        setErrorMessage(
          intl.formatMessage({
            id: 'templates.invitation_link.unfortunately-this-link-has-been-deactivated',
            defaultMessage:
              'Unfortunately, this link has been deactivated. Ask a board or Workspace member to create a new link.',
            description: 'Error message for deactivated invitation link',
          }),
        );
      })
      .catch(ApiError.Unauthorized, (error: Error) => {
        let handled = true;

        if (/organization restricts invites/.test(error.message)) {
          setErrorMessage(
            intl.formatMessage({
              id: 'templates.invitation_link.only-team-members-can-join-this-board',
              defaultMessage:
                "You can't join this board because you're not a member of the board's Workspace.",
              description:
                'Error message for unauthorized user trying to join a board',
            }),
          );
        } else if (
          /invites restricted to managed members/.test(error.message)
        ) {
          setErrorMessage(
            intl.formatMessage({
              id: 'templates.invitation_link.only-managed-members-can-join-this-board',
              defaultMessage:
                "You can't join this board because you're not a managed enterprise member of the board's Workspace's organization.",
              description:
                'Error message for unauthorized user trying to join a board',
            }),
          );
        } else if (
          /invites restricted to org or managed members/.test(error.message)
        ) {
          setErrorMessage(
            intl.formatMessage({
              id: 'templates.invitation_link.only-team-or-managed-members-can-join-this-board',
              defaultMessage:
                "You can't join this board because you're not a member of the board's Workspace or you're not a managed Enterprise member of the board's Workspace's organization.",
              description:
                'Error message for unauthorized user trying to join a board',
            }),
          );
        } else if (
          /Unauthorized to grant licenses to non-enterprise members/.test(
            error.message,
          )
        ) {
          setIsWorkspaceInviteRestrictionError(true);
          setErrorMessage(
            intl.formatMessage({
              id: 'templates.invitation_link.only-enterprise-members-can-access',
              defaultMessage:
                'Only Enterprise members can access this Workspace. To become an Enterprise member, ask an Enterprise admin.',
              description:
                'Error message for unauthorized user trying to join a board',
            }),
          );
        } else {
          handled = false;
        }

        if (handled) {
          Analytics.taskFailed({
            taskName,
            source,
            traceId,
            error,
          });
        } else {
          // No taskFailed here because it may be handled by a different
          // catch block below
          throw error;
        }
      })
      .catch(ApiError.Conflict, (error: Error) => {
        // We couldn't add them, we think because they need to
        // be part of the enterprise
        Analytics.taskFailed({
          taskName,
          source,
          traceId,
          error,
        });
        setErrorMessage(
          intl.formatMessage({
            id: 'templates.invitation_link.before-you-can-join-this-team-you-must-join-the-enterprise-it-belongs-to',
            defaultMessage:
              "Before you can join this Workspace, you'll need to transfer your account to the Enterprise the Workspace belongs to.",
            description:
              'Error message for unauthorized user trying to join a board',
          }),
        );
        setIsEnterpriseError(true);
      })
      .catch(ApiError, (error: Error) => {
        Analytics.taskFailed({
          taskName,
          source,
          traceId,
          error,
        });
        const message = localizeServerError(error);
        // We couldn't add them, let them know why
        setErrorMessage(message);
      })
      .catch((error: Error) => {
        Analytics.taskFailed({
          taskName,
          source,
          traceId,
          error,
        });

        throw error;
      });
  }, [
    analyticsContainers,
    fxJoin,
    modelName,
    setErrorMessage,
    setIsEnterpriseError,
    setIsWorkspaceInviteRestrictionError,
  ]);

  return (
    <>
      <div className="invitation-link-options">
        <Button
          className="nch-button nch-button--primary"
          isDisabled={buttonDisabled}
          onClick={handleOnJoin}
          testId={getTestId<InvitationLinkPageIds>('join-button')}
        >
          {modelName === 'Board' ? (
            <FormattedMessage
              id="templates.invitation_link.join-board"
              defaultMessage="Join board"
              description="Join board button"
            />
          ) : (
            <FormattedMessage
              id="templates.invitation_link.join-team"
              defaultMessage="Join Workspace"
              description="Join team button"
            />
          )}
        </Button>
      </div>
      <div className="little-message">
        <FormattedMessage
          id="templates.invitation_link.different-current-user-safe"
          defaultMessage="Not {currentUserName}? "
          description="Switch accounts link"
          values={{
            currentUserName: <strong key="fullName">{fullName}</strong>,
          }}
        />
        <SwitchAccountsLink returnUrl={returnUrl} />
      </div>
    </>
  );
};
